import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { EditEventAction } from '@connect-services/client/models/edit-event.model';
import { EditModeStateService } from '@connect-services/client/services/edit-mode-state.service';
import { EntityEditPanelGuardService } from '@connect-services/client/services/entity-edit-panel-guard.service';
import { MapEditGeomService } from '@connect-services/client/services/map-edit-geom.service';
import { MapEditModeService } from '@connect-services/client/services/map-edit-mode.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { RessourcesModule } from '@ressources/client/ressources.module';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, RessourcesModule],
    selector: 'app-map-modal-edition',
    templateUrl: './map-modal-edition.component.html',
})
export class MapModalEditionComponent {
    protected readonly ButtonType = ButtonType;
    protected readonly editModeStateService = inject(EditModeStateService);
    protected readonly entityEditPanelGuardService = inject(EntityEditPanelGuardService);
    protected readonly isSubmitButtonDisabled = computed(() => {
        return !this.mapEditModeService.editPermissions().canUpdate || !this.mapEditModeService.hasPendingEdits();
    });
    protected readonly mapEditModeService = inject(MapEditModeService);
    protected readonly projectBloc = inject(ProjectBloc);
    protected readonly redoTooltip = computed(() => {
        if (this.entityEditPanelGuardService.hasPendingEntityEdition()) {
            return 'editMode.undoRedoUnavailableDuringEntityEdition';
        }

        return this.editModeStateService.canReapply() ? 'redoLastAction' : 'nothingToRedo';
    });
    protected readonly submitTooltip = computed(() => {
        return this.isSubmitButtonDisabled() ? undefined : 'editMode.submit.savingChanges';
    });
    protected readonly undoTooltip = computed(() => {
        if (this.entityEditPanelGuardService.hasPendingEntityEdition()) {
            return 'editMode.undoRedoUnavailableDuringEntityEdition';
        }

        return this.editModeStateService.canCancel() ? 'undoLastAction' : 'nothingToUndo';
    });
    private readonly mapEditGeomService = inject(MapEditGeomService);
    private readonly sourcesService = inject(SourcesService);

    protected cancelLastEdit(): void {
        const storedEvent = this.editModeStateService.cancelLastEdit(this.projectBloc.value()!.id);

        if (!storedEvent) {
            return;
        }

        const layerIds = this.getEventActionsLayerIds(storedEvent.actions);
        layerIds.forEach((layerId: string) => this.redrawSource(layerId));
    }

    protected reapplyLastEdit(): void {
        const storedEvent = this.editModeStateService.reapplyLastCanceledEdit(this.projectBloc.value()!.id);

        if (!storedEvent) {
            return;
        }

        const layerIds = this.getEventActionsLayerIds(storedEvent.actions);
        layerIds.forEach((layerId: string) => this.redrawSource(layerId));
    }

    private getEventActionsLayerIds(actions: EditEventAction[]): string[] {
        return Array.from(
            new Set(
                actions
                    .map((action: EditEventAction) => action.layerId)
                    .filter((layerId): layerId is string => !!layerId),
            ),
        );
    }

    private redrawSource(layerId: string): void {
        const source = this.sourcesService.sources().find((source) => source.sourceId === layerId);

        if (!source) {
            return;
        }

        this.mapEditGeomService.drawSource(source);
    }
}
